/**
 * Tunable limits for the Pay Component Import wizard.
 *
 * These keep the wizard's per-interaction work bounded (a page or a chunk) so it
 * scales to large imports. Page size may also be overridden at runtime by the
 * `pay-component-import-page-size` LaunchDarkly flag (clamped to MAX_PAGE_SIZE).
 */

/**
 * Base route path for the wizard. The feature lives under the Onboarding
 * section, so all wizard routes are children of this path. Change it here to
 * relocate the whole wizard.
 */
export const PAY_COMPONENT_IMPORT_BASE_PATH = "/onboarding/pay-component-import";

/** Product-facing title shown in navigation and the wizard page header. */
export const PAY_COMPONENT_IMPORT_DISPLAY_NAME =
  "Assign Pay Components with Values to Employees";

/** Default rows shown per page in the wizard tables. */
export const DEFAULT_PAGE_SIZE = 100;

/** Hard upper bound on rows per page, regardless of user/LD selection. */
export const MAX_PAGE_SIZE = 100;

/** Selectable page sizes offered in the "Rows per page" control (<= MAX_PAGE_SIZE). */
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/**
 * Rows committed per commit-workflow invocation when chunking the (write) import.
 *
 * Must not exceed the API's per-invocation cap (`MAX_ROWS_PER_INVOCATION = 100`
 * in `pay_component_import_models.py`): the API rejects any invocation with more
 * than 100 rows fail-closed at the Pydantic boundary (HTTP 400,
 * `RowCountExceededError`). LADR-038 sets the bulk maximum at 100.
 */
export const COMMIT_CHUNK_SIZE = 100;

/** Maximum rows accepted for a single import (guards client memory). */
export const MAX_IMPORT_ROWS = 50_000;

/** Component-column detection: rows sampled from the file head (keeps the query O(1) in file size). */
export const DETECTION_SAMPLE_ROWS = 200;

/** Detection: skip columns with more distinct values than this (a component column is low-cardinality). */
export const DETECTION_MAX_DISTINCT_PER_COLUMN = 30;

/** Detection: hard cap on total distinct values sent to the discovery endpoint. */
export const DETECTION_MAX_VALUES = 100;

/**
 * Agent-invocation commit (Temporal).
 *
 * The COMMIT step runs as the `pay_component_import` Temporal workflow, invoked
 * via `POST /api/v1/agents/{agentId}/invocations`. The agent id + service
 * account come from the runtime environment (`ONBOARDING_AGENT_ID` /
 * `ONBOARDING_SERVICE_ACCOUNT_ID`, resolved via `useCompanyProvisionAgentId`) —
 * the same onboarding agent used by the company-shell provisioning wizard.
 */
export const PAY_COMPONENT_IMPORT_WORKFLOW = "pay_component_import";

/** Poll interval (ms) while waiting for the commit workflow to finish. */
export const COMMIT_POLL_INTERVAL_MS = 1000;

/** Max poll attempts before giving up on the commit workflow (~3 min at 1s). */
export const COMMIT_POLL_MAX_ATTEMPTS = 180;

/**
 * Initial delay (ms) after starting a commit invocation before the first status
 * GET. A freshly-started workflow needs a moment to register, so polling
 * immediately is a near-guaranteed miss (and, on a flaky backend, a wasted
 * request that can trip the transient-error tolerance below). Delaying once up
 * front lets the first poll land on real state (D7).
 */
export const COMMIT_POLL_INITIAL_DELAY_MS = 1500;

/**
 * Consecutive transient poll failures (network error / HTTP 5xx) tolerated
 * before the commit run gives up on a chunk. The counter resets on any
 * successful poll, so only an *uninterrupted* streak this long aborts the run —
 * a single blip between healthy polls is absorbed (D7). Transient retries do
 * not consume the RUNNING poll-attempt budget (`COMMIT_POLL_MAX_ATTEMPTS`),
 * which guards a different failure mode (a workflow stuck RUNNING).
 */
export const COMMIT_POLL_MAX_TRANSIENT_ERRORS = 5;

/**
 * Base backoff (ms) between retries after a transient poll failure. The wait
 * scales linearly with the consecutive-failure count so a flaky network is
 * given progressively more room to recover before the next attempt.
 */
export const COMMIT_POLL_TRANSIENT_BACKOFF_MS = 1000;

/** Clamp any candidate page size into the valid (1..MAX_PAGE_SIZE) range. */
export function clampPageSize(size: number): number {
  if (!Number.isFinite(size) || size <= 0) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(Math.floor(size), MAX_PAGE_SIZE);
}
